<template>
  <BreakdownSectionComponent
    :title="t('inventory_selection.breakdowns.production_months')"
    :entries="productionMonths"
    :total
  />
  <slot />
  <BreakdownSectionComponent
    v-for="section in shareSections"
    :key="section.key"
    :title="t(`inventory_selection.breakdowns.${section.key}`)"
    :entries="section.entries"
    :total
    :data-testid="`breakdown-${section.key}`"
  />
</template>

<script setup lang="ts">
import { capitalize, sumBy } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { formatMonthYear } from "@/composables/formatUtils";
import type { FlattenedPortfolioItem } from "@/types";
import { RE100_TECHNOLOGIES } from "@/types/technology";

import BreakdownSectionComponent, {
  type BreakdownEntry,
} from "./BreakdownSectionComponent.vue";
import { groupMwh } from "./groupMwh";
import type { ItemMwh } from "./inventorySelectionTypes";

type BreakdownItem = Pick<
  FlattenedPortfolioItem,
  | "id"
  | "count"
  | "production_month"
  | "production_support"
  | "technology_type"
  | "cod_age"
  | "ecolabel_approved"
  | "okpower_new_compatible"
>;

/**
 * The per-attribute MWh breakdowns of a selection: production months, then whatever the default
 * slot adds, then support status, technology, COD age, RE100 and ecolabels. RE100 and ecolabel
 * sections always render (a 0 there is information); the others only when they have rows.
 *
 * `total` is the MWh of `items` (each by `getMwh`), computed once by the panel.
 */
const props = defineProps<{
  items: BreakdownItem[];
  getMwh: ItemMwh;
  total: number;
}>();

const { t } = useI18n();

const COD_BUCKETS = ["cod_le_5", "cod_6_15", "cod_gt_15", "unknown"] as const;

type Group = [label: string, mwh: number];

const byLabel = ([a]: Group, [b]: Group) => a.localeCompare(b);
const byMwhDescending = ([, a]: Group, [, b]: Group) => b - a;

const mwhBy = (keyOf: (item: BreakdownItem) => string): Map<string, number> =>
  groupMwh(props.items, keyOf, props.getMwh);

/** The MWh of `items` per `keyOf` label, as entries in `order`. */
const entriesBy = (
  keyOf: (item: BreakdownItem) => string,
  order: (a: Group, b: Group) => number
): BreakdownEntry[] =>
  [...mwhBy(keyOf)].sort(order).map(([label, mwh]) => ({ label, mwh }));

const sumMwh = (predicate: (item: BreakdownItem) => boolean): number =>
  sumBy(props.items.filter(predicate), props.getMwh);

// Sorted on the ISO month, labelled for reading.
const productionMonths = computed(() =>
  entriesBy((item) => item.production_month, byLabel).map((entry) => ({
    ...entry,
    label: formatMonthYear(entry.label),
  }))
);

// null = registry gave no support data (e.g. CNMC imports) — don't claim "Unsupported"
const supportLabel = (item: BreakdownItem): string =>
  item.production_support === true
    ? t("inventory_selection.breakdowns.supported")
    : item.production_support === false
      ? t("inventory_selection.breakdowns.unsupported")
      : t("inventory_selection.breakdowns.unknown");

const technologyLabel = (item: BreakdownItem): string =>
  item.technology_type
    ? capitalize(item.technology_type)
    : t("inventory_selection.breakdowns.unknown");

const codBucket = (item: BreakdownItem): (typeof COD_BUCKETS)[number] => {
  if (item.cod_age == null) return "unknown";
  if (item.cod_age <= 5) return "cod_le_5";
  if (item.cod_age <= 15) return "cod_6_15";
  return "cod_gt_15";
};

const isRe100Applicable = (item: BreakdownItem): boolean =>
  !!item.technology_type &&
  RE100_TECHNOLOGIES.includes(item.technology_type.toLowerCase()) &&
  item.cod_age != null &&
  item.cod_age <= 15;

const shareSections = computed(() => {
  const codGroups = mwhBy(codBucket);
  const sections = [
    {
      key: "support_status",
      entries: entriesBy(supportLabel, byLabel),
    },
    {
      key: "technology",
      entries: entriesBy(technologyLabel, byMwhDescending),
    },
    {
      key: "commercial_operations_date",
      entries: COD_BUCKETS.filter((bucket) => codGroups.get(bucket)).map(
        (bucket) => ({
          label: t(`inventory_selection.breakdowns.${bucket}`),
          mwh: codGroups.get(bucket)!,
        })
      ),
    },
    {
      key: "re100_applicable",
      entries: [
        {
          label: t("inventory_selection.breakdowns.re100_applicable"),
          mwh: sumMwh(isRe100Applicable),
        },
      ],
    },
    {
      key: "ecolabel_status",
      entries: [
        {
          label: t("inventory_selection.breakdowns.ekoenergy_pre_approved"),
          mwh: sumMwh((item) => item.ecolabel_approved === true),
        },
        {
          label: t("inventory_selection.breakdowns.okpower_new_compatible"),
          mwh: sumMwh((item) => item.okpower_new_compatible === true),
        },
      ],
    },
  ];
  return sections.filter((section) => section.entries.length > 0);
});
</script>
